import React, {useMemo} from 'react';
import {useParams, useNavigate} from 'react-router';
import styles from './FundraiserCommunicationTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, Icon} from '@gravity-ui/uikit';
import {Picture} from '@gravity-ui/icons';
import {DataTable, formatDateToLocalDate} from '../../shared';
import {CommunicationMessageModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';

interface FundraiserCommunicationTableProps {
  messages: CommunicationMessageModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
}

export const FundraiserCommunicationTable = ({
  messages,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: FundraiserCommunicationTableProps) => {
  const {id: appealId = ''} = useParams<{id: string}>();
  const navigate = useNavigate();
  const SKELETON_ROWS = 5;

  const columns = useMemo<TableColumnConfig<CommunicationMessageModel>[]>(() => {
    return [
      {
        id: 'subject',
        name: 'Заголовок',
        width: '40%',
        template: (item) => (
          <div className={styles.subjectCell}>
            {item.hasImage && <Icon data={Picture} size={16} className={styles.subjectCellIcon} />}
            <Text variant="body-2" color="primary">
              {item.subject}
            </Text>
          </div>
        ),
      },
      {
        id: 'createdAt',
        name: 'Дата',
        width: '20%',
        template: (item) => (
          <Text variant="body-2" color="secondary">
            {formatDateToLocalDate(item.createdAt)}
          </Text>
        ),
      },
    ];
  }, []);

  const handleRowClick = (message: CommunicationMessageModel) => {
    navigate(`/fundraisings/appeals/${appealId}/communication/${message.id}`);
  };

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<CommunicationMessageModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={messages || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Уведомления не найдены"
        emptyStateDescription="Уведомления не найдены. Попробуйте изменить запрос"
        onRowClick={handleRowClick}
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
